import { useEffect, useMemo, useRef } from 'react';
import { Timeframe } from '@ankr.com/chains-list';

import { Tab, useTabs } from 'modules/common/hooks/useTabs';
import { USAGE_FULL_TIMEFRAME_LIST } from 'domains/chains/constants/timeframes';
import { useAccount } from 'modules/account/hooks/useAccount';

export interface TimeframeResult {
  timeframe: Timeframe;
  timeframeTabs: Tab<Timeframe>[];
}

interface UseTimeframeParams {
  timeframes?: Timeframe[];
  initialTimeframe?: Timeframe;
  shouldResetOnGroupChange?: boolean;
}

export const useTimeframe = ({
  initialTimeframe = Timeframe.Month,
  shouldResetOnGroupChange = false,
  timeframes = USAGE_FULL_TIMEFRAME_LIST,
}: UseTimeframeParams): TimeframeResult => {
  const tabs = useMemo(
    () => timeframes.map<Tab<Timeframe>>(id => ({ id })),
    [timeframes],
  );

  const [timeframeTabs, timeframeTab, setTabId] = useTabs<Timeframe>({
    initialTabID: initialTimeframe,
    tabs,
  });

  const { group } = useAccount();
  const groupRef = useRef(group);

  useEffect(() => {
    const isGroupChanged = groupRef.current !== group;

    if (isGroupChanged && shouldResetOnGroupChange) {
      setTabId(initialTimeframe);

      groupRef.current = group;
    }
  }, [
    group,
    initialTimeframe,
    setTabId,
    shouldResetOnGroupChange,
    timeframeTab,
  ]);

  return { timeframe: timeframeTab?.id || tabs[0].id, timeframeTabs };
};
